import React, { useRef, useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useFirebase } from 'react-redux-firebase';
import { Breadcrumb, Button, Divider, List, Avatar, notification, Modal, Tag, Input } from 'antd';
import { EditOutlined, LockOutlined, CloseCircleOutlined, CheckCircleOutlined, SwapOutlined, DeleteOutlined, FolderFilled, FileFilled } from '@ant-design/icons';
import { v4 as uuidv4 } from 'uuid';
import { documents as documentsActions, files as filesActions } from '../../actions/database/documents';
import { logClient } from '../../lib/client_log';
// Carpeta cerrada: supervisora/root actúan sobre la carpeta; abrir y cerrar
// exige además ser supervisora (lib/closed_folder_perms.js).
import { closedFolderAccess, canCloseFolderIn, canOpenFolderIn } from '../../lib/closed_folder_perms';
import { loadCompanyAccess, sameCompany, listingBlocked, effectiveAt, visibleChildren, folderHidden, showsFolders, showsFiles, zipAllows } from '../../lib/company_access';
import { canRestrict } from '../../lib/rank';
import { isScoped } from '../../lib/user_scope';
import { canSeeAccountedMark, seesAccountedAlways } from '../../lib/accounted_visibility';
import { canSeeBulkAccounting } from '../../lib/bulk_accounting_visibility';
import { needsAccountingRead, filterUnaccounted, isMarkPending, markPendingRows } from '../../lib/unaccounted_filter';
import { splitFolderDocs, buildFileRows, hydrateRows, folderArrayEntries, patchFileRow, removeFileRow } from '../../lib/folder_listing';
import { viewKey, pageFor, pageState, syncPageState } from '../../lib/list_page';
import { getFolderCache, setFolderCache, invalidateFolder } from '../../lib/folder_cache';
import { FILES_SUBCOLLECTION } from '../../lib/folder_files';

import { DocumentUpload } from './upload';
import { useDocuments } from './context';
import DownloadFolder from './download_folder';
import { BulkUploader } from './bulk_uploader';
import { BulkAccounting } from './bulk_accounting';
import { withTimeout } from './withTimeout';
import { getChildCollection } from './folder_ref';

// Si Firestore no responde en este tiempo, el spinner se libera con un toast
// de error en lugar de quedar girando indefinidamente.
const FOLDER_LOAD_TIMEOUT_MS = 15000;

// Formatea un Timestamp de Firestore o Date a fecha legible es-CO.
// Devuelve null si el valor no existe (ej. carpetas creadas antes de que
// se guardara createdAt).
const formatUploadDate = (v) => {
    const d = v?.toDate ? v.toDate() : (v instanceof Date ? v : null);
    return d ? d.toLocaleDateString('es-CO', { year: 'numeric', month: 'long', day: 'numeric' }) : null;
};

export const useListFolder = ({ form }) => {
  const tokenRef = useRef();
  // useFirebase devuelve el factory; lo necesitamos para getChildCollection
  // cuando reference.path llega como string (sembrado vía admin SDK).
  const firebase = useFirebase();

    // Funcion que permite ejecutar todas las acciones del estado de redux
    const dispatch = useDispatch();
    // Selecciona los reductores
    const auth = useSelector(state => state.get('authenticate').get('login'));
    // Rol global del usuario. Ya NO es piso del efectivo (spec 2026-10-06): el
    // motor lo usa solo como defecto de la empresa sin matriz; aquí además
    // decide los gates de ROL (cerrar/abrir, carpeta cerrada).
    const globalRolePerms = useSelector(state => state.get('roles').get('roles').roles);
    // Manejo de los estados del componente
    const [folders, setFolders] = useState({ reference: undefined, visible: false, loading: false, name: '', update: false, isLoad: false });
    const [routes, setRoutes] = useState([]);
    // Las rutas también en un ref: loadFolders corre en el mismo tick que el
    // setRoutes del clic y necesita las entradas (globales) de los ancestros.
    const routesRef = useRef([]);
    const setRoutesTracked = (v) => {
        if (typeof v !== 'function') routesRef.current = v;
        setRoutes(prev => {
            const next = typeof v === 'function' ? v(prev) : v;
            routesRef.current = next;
            return next;
        });
    };
    const [reference, setReference] = useState();
    const [userRoles, setUserRoles] = useState({ documents: {}, folders: {} });
    // Empresa abierta (spec 2026-10-07): root SOLO por documents.root; isAdmin
    // = Usuarios[].isAdmin en esta empresa; linked = vinculado a ella. La
    // matriz de empresa y las restricciones se leen UNA vez al entrar
    // (lib/company_access) y otra vez con «Actualizar» o al guardar una
    // restricción. El efectivo es el mismo en toda carpeta no restringida.
    const [company, setCompany] = useState({ cid: null, isRootUser: false, isAdmin: false, linked: false });
    const companyRef = useRef(company);
    const setCompanyCtx = (c) => { companyRef.current = c; setCompany(c); };
    const accessRef = useRef({ key: null, promise: null, value: null });
    const accessFor = ({ force = false } = {}) => {
        const c = companyRef.current;
        const uid = auth?.user?.uid;
        const key = c.cid && uid ? `${c.cid}|${uid}|${c.isRootUser}` : null;
        if (!key) return Promise.resolve(null);
        if (force || accessRef.current.key !== key) {
            const promise = loadCompanyAccess({
                db: firebase.firestore(),
                cid: c.cid,
                uid,
                isRootUser: c.isRootUser,
                // Quien puede restringir (rango ≥ 60) necesita todas para el panel.
                readsAll: canRestrict(globalRolePerms),
            }).then((value) => {
                if (accessRef.current.promise === promise) accessRef.current.value = value;
                return value;
            });
            accessRef.current = { key, promise, value: null };
        }
        return accessRef.current.promise;
    };
    const [isClosing, setIsClosing] = useState(false);
    const [isUpload, setIsUpload] = useState(false);
    const [isRootFolder, setIsRootFolder] = useState(true);
    const [isRoot, setIsRoot] = useState(false);
    const [isAdmin, setIsAdmin] = useState(false);
    const [permissions, setPermissions] = useState({ visible: false, users: [], folder: {} });
    // `source` son las filas de CARPETA (JSX, se reconstruye siempre).
    // `rows` son las FileRow de archivo: datos, no JSX (ver lib/folder_listing).
    const [data, setData] = useState({ loading: false, columns: [], source: [], rows: [], hydrating: false });
    // Generación de carga: cada loadFolders la incrementa. Las hidrataciones en
    // vuelo solo aplican sus resultados si su generación sigue vigente — si el
    // usuario navegó a otra carpeta, se descartan.
    const loadGenRef = useRef(0);
    // Filas vivas de la carpeta actual, para hidratar sin depender del closure
    // de setData (que puede ir por detrás en cargas rápidas).
    const rowsRef = useRef([]);
    // Rutas cuya precarga en segundo plano ya está en vuelo, para no lanzarla
    // dos veces si el mouse entra y sale de la misma fila.
    const prefetchingRef = useRef(new Set());
    const [file, setFile] = useState({ visible: false, isLoad: false, reference: undefined, fields: {} });

    /**
     * Lee el doc que declara la carpeta (o la raíz de la empresa) y calcula el
     * efectivo de la carpeta con el motor. Devuelve también
     * el data del doc (ahí vive su files[]) y el índice de entradas.
     * @param {object} reference CollectionReference de la carpeta, o el
     *        DocumentReference de la raíz folders/{cid}
     */
    const getFolderInfo = async reference => {
        const ref = reference?.doc ? reference?.parent : reference;
        logClient({ event: 'dbg.getFolderInfo.start', level: 'info', message: 'getFolderInfo start', data: { refPath: ref?.path, uid: auth?.user?.uid } });
        // La empresa de esta carga: si cambia mientras se espera, se descarta.
        const c = companyRef.current;
        const stale = () => {
            if (sameCompany(c, companyRef.current)) return;
            const err = new Error('Carga de otra empresa descartada.');
            err.code = 'stale-company';
            throw err;
        };
        try {
            const fields = await withTimeout(ref.get(), FOLDER_LOAD_TIMEOUT_MS, 'Cargar info de carpeta');
            stale();
            const data = fields.data();
            const isCollection = !!reference?.doc;
            const current = isCollection && Array.isArray(data?.folders)
                ? (data.folders.find(f => f?.reference?.id === reference.id) || null)
                : null;
            setIsUpload(current?.isUpload || false);
            setIsClosing(current?.close?.date ? true : false);

            const collectionPath = isCollection ? reference.path : null;
            const access = await accessFor();
            stale();
            // Spec §3: si las restricciones no se pudieron leer, no se lista
            // nada (nunca se muestra todo por un fallo de lectura).
            if (listingBlocked(access)) {
                const err = new Error('No se pudieron leer las restricciones de acceso de esta empresa.');
                err.code = 'restrictions-unavailable';
                throw err;
            }
            const effectiveRoles = effectiveAt({ access, role: globalRolePerms, isAdmin: c.isAdmin, collectionPath });
            setUserRoles(effectiveRoles);
            logClient({ event: 'dbg.getFolderInfo.roles', level: 'info', message: 'efectivo de la carpeta', data: { collectionPath, foldersRead: effectiveRoles?.folders?.read === true, matrix: access.companyMatrixState, restricciones: access.restrictions.length } });
            return { roles: effectiveRoles, folderData: data || null, access, collectionPath };
        } catch (error) {
            if (error?.code === 'stale-company') throw error;
            setIsUpload(false);
            setIsClosing(false);
            logClient({ event: 'dbg.getFolderInfo.folder_error', level: 'error', message: 'getFolderInfo ERROR', data: { code: error?.code, errMessage: error?.message } });
            throw error;
        }
    };


    /**
     * Metodo que permite eliminar una carpeta
     */
    const deleteFolder = async ({ folder, roles, token }) => {

        // Validar el token server-side: el cliente ya no puede leer `tokens`
        // (solo super admin); la acción de delete valida contra el token
        // activo y registra el uso.
        dispatch(
            documentsActions.delete({
                roles,
                item: Object.assign({}, folder, { token }),
                isValidateToken: true
            })
        ).then(result => {
            notification.info({
                message: 'Eliminación iniciada',
                description: 'La carpeta se está eliminando en segundo plano; el contenido quedará en la papelera.',
                placement: 'topLeft'
            });
            form.resetFields();
            setFolders({ ...folders, isLoad: true });
        }).catch(error => {
            const code = error?.code || '';
            let description = error?.message || 'Se presento un error al tratar de eliminar la carpeta';

            // La callable lanza 'failed-precondition' con mensaje claro para token inválido.
            if (code === 'failed-precondition' && /token/i.test(error?.message || '')) {
                description = error.message;
            } else if (code === 'permission-denied' || /permission/i.test(error?.message || '')) {
                description = 'No tienes permisos suficientes para eliminar esta carpeta o su contenido. ' +
                              'Pide al administrador del cliente que te conceda permiso de "Editar" (update) ' +
                              'sobre la carpeta raíz — es necesario para borrar archivos descendientes.';
            } else if (/index/i.test(error?.message || '')) {
                description = 'Firestore requiere un índice que no está creado. Contacta al administrador del sistema.';
            } else if (code === 'unavailable' || /offline|network/i.test(error?.message || '')) {
                description = 'Sin conexión o Firestore no disponible. Intenta de nuevo en unos segundos.';
            } else if (code === 0 || /Permisos insuficientes/i.test(error?.message || '')) {
                description = error.message;
            }

            console.error('[deleteFolder] Error:', { code, message: error?.message, error });
            notification.error({
                message: 'No se pudo eliminar la carpeta',
                description,
                placement: 'topRight',
                duration: 8,
            });
        });
    };

    /**
     * Metodo que permite cerrar una carpeta
     * @param {object} folder 
     */
    const folderClosing = ({ folder, roles, allowed }) => {
        dispatch(documentsActions.close({ roles, item: folder, allowed })).then(({ data }) => {
            form.resetFields();
            setFolders({ ...folders, isLoad: true });
        }).catch(error => {
            notification.error({
                message: 'Error',
                description: 'Se presento un error al tratar de cerrar la carpeta',
                placement: 'topRight'
            })
        })
    };

    /**
     * Metodo que permite abrir una carpeta
     * @param {object} folder 
     */
    const folderOpening = ({ folder, roles, allowed }) => {
        dispatch(documentsActions.open({ roles, item: folder, allowed })).then(({ data }) => {
            form.resetFields();
            setFolders({ ...folders, isLoad: true });
        }).catch(error => {
            notification.error({
                message: 'Error',
                description: 'Se presento un error al tratar de cerrar la carpeta',
                placement: 'topRight'
            })
        });
    };

    /**
     * Convierte una FileRow (datos) en el item del List (JSX).
     *
     * `row.entry` puede venir del array files[] (0 lecturas) o del doc ya
     * leído; `row.full` está cuando ya se leyó el doc completo, y en ese caso
     * abrir el archivo no cuesta ninguna lectura. Una sola función para las
     * tres clases de fila (del array, hidratada, fallida) para que rendericen
     * idénticas.
     */
    // flagMarkPending: en el filtro «Sin contabilizar», distinguir los que ya
    // tienen la contabilización cargada y solo les falta la marca.
    const fileItem = (row, { flagMarkPending = false } = {}) => {
        if (row.failed) return {
            key: row.fileId,
            sorted: row.fileId,
            avatar: <FileFilled style={{ color: '#bfbfbf' }} />,
            actions: [],
            title: <Tag color="default">Archivo no disponible</Tag>,
            description: <span style={{ color: '#8c8c8c' }}>No se pudo leer este documento. Puede ser un permiso o un archivo eliminado.</span>,
        };
        const entry = row.entry;
        return {
            key: row.fileId,
            sorted: entry.name,
            avatar: <FileFilled style={{ color: '#1890ff' }} />,
            actions: [],
            title: (
                <Button type="link" style={{ paddingLeft: 0 }}
                    onClick={async () => {
                        // Si la fila ya tiene el doc completo (lo normal: se lee
                        // al hidratar la página), el drawer abre sin lecturas.
                        if (row.full && row.ref) {
                            setFile({
                                visible: true,
                                isLoad: true,
                                reference: { id: row.fileId, ref: row.ref, exists: true, data: () => row.full },
                                fields: row.full
                            });
                            return;
                        }
                        try {
                            const snap = await row.ref.get();
                            setFile({ visible: true, isLoad: true, reference: snap, fields: snap.data() });
                        } catch (error) {
                            notification.error({
                                message: 'No se pudo abrir el archivo',
                                description: 'No fue posible leer el detalle del documento. Intenta de nuevo.',
                                placement: 'topRight'
                            });
                        }
                    }} >
                    <Tag color="#f50">{entry.name}</Tag>
                </Button>
            ),
            description: (
                <>
                    {formatUploadDate(entry.date_of_upload)
                        ? <Tag color='blue'>Fecha de subida: {formatUploadDate(entry.date_of_upload)}</Tag>
                        : row.fileId}
                    {/* Marca manual de contabilizado, visible desde el listado.
                        Es gestión interna entre contadores: la ve quien puede
                        contabilizar (ver lib/accounted_visibility.js). */}
                    {canSeeAccountedMark({
                        accounted: entry.accounted,
                        isSuperAdmin: isRoot,
                        isAdmin,
                        canAccount: userRoles?.documents?.accounting,
                    })
                        ? <Tag color='green'>Contabilizado{formatUploadDate(entry.accounted.at) ? `: ${formatUploadDate(entry.accounted.at)}` : ''}</Tag>
                        : null}
                    {flagMarkPending && isMarkPending(row)
                        ? <Tag color='orange'>Contabilización cargada · sin marca</Tag>
                        : null}
                </>
            ),
            data: entry
        };
    };

    // Deja la cache de sesion al dia con las filas vivas.
    const syncCache = () => {
        const path = reference?.path;
        const cached = path ? getFolderCache(path) : null;
        if (cached) setFolderCache(path, { ...cached, fileRows: rowsRef.current });
    };

    /**
     * Actualiza una fila del listado en sitio, sin recargar la carpeta.
     * Lo usa el drawer de detalle para refrescar la etiqueta de contabilizado
     * tras marcar el archivo o adjuntarle un soporte: antes esas acciones
     * recargaban la carpeta entera para cambiar un tag.
     * @param {string} fileId
     * @param {object} patch   ej. { accounted } o { hasAccounting }
     */
    const patchRow = (fileId, patch) => {
        rowsRef.current = patchFileRow(rowsRef.current, fileId, patch);
        setData(prev => ({ ...prev, rows: rowsRef.current }));
        syncCache();
    };

    /** Quita una fila del listado en sitio (archivo eliminado). */
    const removeRow = (fileId) => {
        rowsRef.current = removeFileRow(rowsRef.current, fileId);
        setData(prev => ({ ...prev, rows: rowsRef.current }));
        syncCache();
    };

    /**
     * Resuelve la carpeta COMPLETA: cruza los punteros con `files[]` y lee de
     * una sola vez los que no tengan entrada, en tandas paralelas.
     *
     * Se hace completo a propósito, no por página: el listado conserva el orden
     * alfabético de siempre, y para ordenar por nombre hay que conocerlos
     * todos. Pintar por páginas obligaba a ordenar por fecha de subida y a
     * mostrar filas "Cargando…" — dos cambios que el usuario notaría. Se paga
     * una vez por carpeta y sesión: la caché hace gratis todo lo demás.
     *
     * @returns {Promise<object[]>} las FileRow ya resueltas
     */
    const resolveRows = async (collRef, folderData, docs) => {
        const { pointerDocs } = splitFolderDocs(docs);
        let storeDocs = [];
        if (folderData?.filesOverflow === true && collRef?.parent) {
            try {
                const st = await collRef.parent.collection(FILES_SUBCOLLECTION).get();
                storeDocs = st.docs.map(d => d.data());
            } catch (e) {
                console.warn('No se pudo leer files-store:', e?.message);
            }
        }
        const rows = buildFileRows({
            pointerDocs,
            arrayEntries: folderArrayEntries(folderData, storeDocs),
        });
        const pendientes = rows.filter(r => !r.entry);
        if (pendientes.length === 0) return rows;

        const resueltas = new Map();
        const { reads, failed } = await hydrateRows({
            rows: pendientes,
            onRow: (row) => resueltas.set(row.fileId, row),
        });
        logClient({ event: 'dbg.loadFolders.resolved', level: 'info', message: 'carpeta resuelta', data: { archivos: rows.length, leidos: pendientes.length, reads, failed } });
        return rows.map(r => resueltas.get(r.fileId) || r);
    };

    /**
     * Precarga una subcarpeta en segundo plano al pasar el mouse por su fila.
     * Para cuando el usuario hace clic, ya está en la caché y entra al
     * instante. Solo cuesta lo que el usuario estaba a punto de abrir.
     */
    const prefetchFolder = async (collRef) => {
        const path = collRef?.path;
        if (!path || getFolderCache(path) || prefetchingRef.current.has(path)) return;
        prefetchingRef.current.add(path);
        try {
            const [parentSnap, docs] = await Promise.all([collRef.parent.get(), collRef.get()]);
            const folderData = parentSnap.data() || null;
            const rows = await resolveRows(collRef, folderData, docs.docs);
            setFolderCache(path, { folderData, fileRows: rows });
        } catch (e) {
            // Sin permiso o sin red: no se precarga y ya. El clic hará su
            // propia carga con su manejo de errores.
            prefetchingRef.current.delete(path);
        }
    };

    /**
     * Compatibilidad con el buscador de carpeta: las filas ya vienen completas,
     * así que solo las devuelve.
     * @returns {Promise<object[]>}
     */
    const ensureAllNames = async () => rowsRef.current;

    /**
     * Filtro "Sin contabilizar": lee files/{id} solo de las filas cuyo array
     * no dice si tienen contabilización (lib/unaccounted_filter). Se paga una
     * vez por carpeta: las filas quedan completas en la caché de sesión. Si el
     * usuario cambió de carpeta mientras leía, el resultado se descarta.
     */
    const ensureAccountingKnown = async () => {
        const pendientes = rowsRef.current.filter(needsAccountingRead);
        if (pendientes.length === 0) return;
        const gen = loadGenRef.current;
        const resueltas = new Map();
        await hydrateRows({ rows: pendientes, onRow: (row) => resueltas.set(row.fileId, row) });
        if (gen !== loadGenRef.current) return;
        rowsRef.current = rowsRef.current.map(r => resueltas.get(r.fileId) || r);
        setData(prev => ({ ...prev, rows: rowsRef.current }));
        syncCache();
    };

    /**
     * Metodo que permite cargar las carpetas por referencia
     * @param {object} folder
     * @param {object} [opts]
     * @param {boolean} [opts.force]  true = mutación: invalida la caché de esta
     *        carpeta antes de leer. false/omitido = navegación: usa la caché.
     */
    const loadFolders = (folder, { force = false, refreshAccess = false } = {}) => new Promise((resolve, reject) => {
        if (!folder) {
            return;  // Para asegurarse de que el código no continúe si no hay un folder.
        }

        const cachePath = folder?.path || null;
        if (force && cachePath) invalidateFolder(cachePath);

        // Generación y empresa de ESTA carga, fijadas antes de cualquier
        // lectura: una carga vieja (otra carpeta u otra empresa) nunca pinta.
        const gen = ++loadGenRef.current;
        const loadCompany = companyRef.current;
        const isCurrent = () => gen === loadGenRef.current && sameCompany(loadCompany, companyRef.current);

        const isThisRoot = folder?.parent?.parent ? false : true;
        setReference(folder);
        setIsRootFolder(isThisRoot);
        setData({ loading: true, columns: [], source: [] });

        logClient({ event: 'dbg.loadFolders.start', level: 'info', message: 'loadFolders start', data: { folderPath: folder?.path, isThisRoot } });
        // «Actualizar» y guardar una restricción vuelven a leer la empresa.
        const accessReady = refreshAccess ? accessFor({ force: true }) : Promise.resolve();
        accessReady.then(() => getFolderInfo(folder))
            .then(({ roles, folderData, access }) => {
                logClient({ event: 'dbg.loadFolders.after_getFolderInfo', level: 'info', message: 'roles tras getFolderInfo', data: { foldersRead: roles?.folders?.read === true } });
                if (!isCurrent()) { resolve(); return undefined; }
                return withTimeout(folder.get(), FOLDER_LOAD_TIMEOUT_MS, 'Listar contenido de carpeta').then(async docs => {
                    if (docs.data && typeof docs.data === 'function') {
                        const dataVal = docs.data();
                        logClient({ event: 'dbg.loadFolders.docs_loaded', level: 'info', message: 'docs.get() OK', data: { isArrayFolders: Array.isArray(dataVal?.folders), foldersCount: (dataVal?.folders || []).length, rolesFoldersRead: roles?.folders?.read === true, willRender: Array.isArray(dataVal?.folders) && (roles?.folders?.read === true) } });
                    } else {
                        logClient({ event: 'dbg.loadFolders.docs_no_data', level: 'warn', message: 'docs.get() sin .data function', data: { hasDocs: !!docs?.docs } });
                    }
                    const items = [];
                    // `roles` es el efectivo de ESA hija: tapa a propósito el
                    // de la carpeta actual.
                    const folderActions = (folder, roles) => {
                        const actions = [];
                        const isRootFolder = folder.reference.path?.parent?.parent ? false : true;
                        // Carpeta de primer nivel: editar, cerrar y eliminar los hace root
                        // o quien tiene rango >= 60 (contador, supervisora). El rol admin,
                        // el auxiliar y los clientes siguen sin tocarlas (Juan, 2026-10-08).
                        const manageTopLevel = isRoot || canRestrict(globalRolePerms);
                        const isClosing = folder?.close?.date ? true : false;
    
                        if (roles?.folders?.read) {
                            if (roles.folders.update && closedFolderAccess({ globalRole: globalRolePerms, isClosing }).folderWrite) {
                                if (manageTopLevel || !isRootFolder) {
                                    actions.push(
                                        <Button type="link" style={{ paddingLeft: 0 }}
                                            onClick={() => {
                                                form.resetFields();
                                                form.setFieldsValue({
                                                    folder: folder.name,
                                                    description: folder.description,
                                                    isUpload: folder.isUpload
                                                });
                                                setFolders({ reference: folder, visible: true, loading: false, update: true });
                                            }}
                                        > <EditOutlined style={{ marginRight: 8 }} /> Editar carpeta </Button>
                                    );
                                }
                            }
    
                            // «Restringir acceso» (spec 2026-10-07 §3): rango ≥ 60
                            // (contador, supervisora, root); con alcance, solo en
                            // sus empresas. Ni el rol admin, ni el auxiliar, ni el
                            // administrador de empresa.
                            if (canRestrict(globalRolePerms) && (!isScoped(globalRolePerms) || companyRef.current.linked)) {
                                actions.push(
                                    <Button type="link" style={{ paddingLeft: 0 }}
                                        onClick={() => setPermissions({ visible: true, users: permissions.users, folder })} >
                                        <LockOutlined /> Restringir acceso
                                    </Button>
                                );
                            }

                            if (roles.folders.download) {
                                // ZIP (spec 2026-10-07): el efectivo es el de la empresa en
                                // todo el subárbol; lo restringido se poda entero.
                                actions.push(
                                    <><DownloadFolder folder={folder}
                                        filesAllowed={async () => zipAllows(roles)}
                                        pruneSubtree={async ({ collectionPath }) => folderHidden({ access, collectionPath })} /></>
                                );
                            }
    
                            // Abrir y cerrar (lib/closed_folder_perms.js): el rol más
                            // users.Carpetas, salvo que la matriz de la empresa decida
                            // otra cosa (permissions.carpetas). Root siempre.
                            const closeAllowed = canCloseFolderIn({ globalRole: globalRolePerms, companyMatrix: access?.companyMatrix });
                            const openAllowed = canOpenFolderIn({ globalRole: globalRolePerms, companyMatrix: access?.companyMatrix });
                            if (manageTopLevel || !isRootFolder) {
                                if (!isClosing && closeAllowed) actions.push(
                                    <Button type="link"
                                        style={{ color: '#FAAD14', paddingLeft: 0 }}
                                        onClick={() => {
                                            Modal.confirm({
                                                title: `Cerrar carpeta ${folder.name}`,
                                                content: 'Esta seguro de cerrar la carpeta seleccionada?',
                                                okText: 'Cerrar',
                                                okType: 'warning',
                                                cancelText: 'Cancelar',
                                                onOk: () => folderClosing({ folder, roles: roles.folders, allowed: closeAllowed })
                                            });
                                        }}
                                    > <CloseCircleOutlined style={{ marginRight: 8 }} /> Cerrar </Button>
                                ); else if (isClosing && openAllowed) actions.push(
                                    <Button type="link"
                                        style={{ color: '#FAAD14', paddingLeft: 0 }}
                                        onClick={() => {
                                            Modal.confirm({
                                                title: `Abrir carpeta ${folder.name}`,
                                                content: 'Esta seguro de abrir la carpeta seleccionada?',
                                                okText: 'Abrir',
                                                okType: 'warning',
                                                cancelText: 'Cancelar',
                                                onOk: () => folderOpening({ folder, roles: roles.folders, allowed: openAllowed })
                                            });
                                        }}
                                    > <CheckCircleOutlined style={{ marginRight: 8 }} /> Abrir </Button>
                                );
                            }
    
                            if (roles.folders.delete && closedFolderAccess({ globalRole: globalRolePerms, isClosing }).folderWrite) {
                                if (manageTopLevel || !isRootFolder) {
                                    actions.push(
                                        <Button type="link"
                                            style={{ color: '#F5222D', paddingLeft: 0 }}
                                            onClick={() => {
                                                Modal.confirm({
                                                    title: `Eliminar carpeta ${folder.name}`,
                                                    content: (
                                                        <div>
                                                            Ingrese el token para eliminar la carpeta
                                                            <br />
                                                            <Input placeholder="Token"
                                                                prefix={<SwapOutlined style={{ color: 'rgba(0,0,0,.25)' }} />}
                                                                ref={tokenRef} />
                                                        </div>
                                                    ),
                                                    okText: 'Eliminar',
                                                    okType: 'danger',
                                                    cancelText: 'Cancelar',
                                                    onOk: () => deleteFolder({ folder, roles: roles.folders,token: tokenRef.current.input.value })
                                                });
                                            }}
                                        > <DeleteOutlined style={{ marginRight: 8 }} /> Eliminar </Button>
                                    );
                                }
                            }
                        }
    
                        return actions;
                    };
    
                    // Fila de una subcarpeta. `roles` es el efectivo de la
                    // empresa: el mismo en toda carpeta que se muestra.
                    const buildFolderRow = folder => ({
                        key: uuidv4(),
                        sorted: folder.name,
                        avatar: <FolderFilled />,
                        description: (
                            <>
                                <p>{folder.description}</p>
                                {formatUploadDate(folder.createdAt) ?
                                    <Tag color='blue'>
                                        Fecha de subida: {formatUploadDate(folder.createdAt)}
                                    </Tag> : null}
                                {folder?.close?.date ?
                                    <Tag color='volcano'>
                                        Fecha de cierre: {
                                            folder.close.date?.toDate().toLocaleDateString('es-CO', {
                                                weekday: 'long',
                                                year: 'numeric',
                                                month: 'long',
                                                day: 'numeric'
                                            })
                                        }
                                    </Tag> : null}
                            </>
                        ),
                        actions: folderActions(folder, roles),
                        // Precarga en segundo plano: al pasar el mouse por la
                        // fila se va cargando la subcarpeta, así el clic entra
                        // al instante.
                        onHover: () => {
                            const childColl = getChildCollection(firebase, folder.reference);
                            if (childColl) prefetchFolder(childColl);
                        },
                        title: (
                            <Button type="link" style={{ paddingLeft: 0 }}
                                onClick={() => {
                                    setIsUpload(folder.isUpload);
                                    setIsClosing(folder.close ? folder.close.date ? true : false : false);
                                    const childColl = getChildCollection(firebase, folder.reference);
                                    if (!childColl) {
                                        notification.error({
                                            message: 'Carpeta inválida',
                                            description: `La carpeta "${folder.name}" tiene una referencia mal formada. Contacta soporte.`,
                                            placement: 'topRight'
                                        });
                                        return;
                                    }
                                    const route = { name: folder.name, reference: childColl, entry: folder };
                                    // El ref se adelanta al render: loadFolders lo lee ya.
                                    routesRef.current = [...routesRef.current, route];
                                    setRoutesTracked(rs => [...rs, route]);
                                    loadFolders(childColl);
                                }}
                            > <Tag color='#f50'>{folder.name}</Tag> </Button>
                        )
                    });

                    // Hijas (spec 2026-10-07): con «Leer» de la empresa, todas
                    // menos las restringidas para este usuario (y lo de abajo).
                    const paintChildren = (entries) => {
                        if (!showsFolders(roles)) return;
                        visibleChildren({ access, entries }).forEach(entry => items.push(buildFolderRow(entry)));
                    };

                    if (docs.data) {
                        // Raíz del cliente (DocumentSnapshot): SOLO carpetas. No
                        // agregar filas de archivo aquí: por el off-by-one de
                        // upload.js:80, folders/{cid}.files[] trae entradas de
                        // las subcarpetas de primer nivel, y pintarlas en la raíz
                        // sería una regresión.
                        if (Array.isArray(docs.data()?.folders)) {
                            await paintChildren(docs.data().folders);
                        }
                    } else if (Array.isArray(docs.docs)) {
                        const { folderDocs } = splitFolderDocs(docs.docs);

                        // 1) Subcarpetas: 0 lecturas, ya vienen en folders[].
                        await paintChildren(folderDocs.flatMap(doc => doc.data()['folders'] || []));

                        // 2) Filas de archivo, resueltas COMPLETAS antes de
                        //    pintar: el listado conserva el orden alfabético de
                        //    siempre, y para ordenar por nombre hay que
                        //    conocerlos todos. Se paga una vez por carpeta y
                        //    sesión — la caché hace gratis las visitas
                        //    siguientes.
                        let rows = [];
                        let desdeCache = false;
                        // Archivos: solo con «Leer» de la carpeta y de archivos
                        // (una carpeta «de paso» muestra solo el camino).
                        if (showsFiles(roles)) {
                            const cached = cachePath ? getFolderCache(cachePath) : null;
                            if (cached) {
                                rows = cached.fileRows;
                                desdeCache = true;
                            } else {
                                rows = await resolveRows(folder, folderData, docs.docs);
                                if (cachePath) setFolderCache(cachePath, { folderData, fileRows: rows });
                            }
                        }

                        // El usuario pudo navegar a otra carpeta mientras esto
                        // resolvía: si la generación cambió, se descarta.
                        if (!isCurrent()) { resolve(); return; }

                        rowsRef.current = rows;
                        setData({ loading: false, columns: [], source: items, rows, hydrating: false });
                        logClient({ event: 'dbg.loadFolders.painted', level: 'info', message: 'listado pintado', data: { carpetas: items.length, archivos: rows.length, desdeCache } });
                        resolve();
                        return;
                    }

                    if (!isCurrent()) { resolve(); return; }
                    rowsRef.current = [];
                    setData({ loading: false, columns: [], source: items, rows: [], hydrating: false });
                    resolve();
                }).catch(error => {
                    console.error('Error retrieving folders:', error);
                    reject(error);
                });
            })
            .catch(error => {
                if (error?.code === 'stale-company') {
                    // Si nadie la reemplazó, no se deja el spinner colgado;
                    // si hay una carga más nueva, ella limpia el estado.
                    if (gen === loadGenRef.current) {
                        rowsRef.current = [];
                        setData({ loading: false, columns: [], source: [], rows: [], hydrating: false });
                    }
                    resolve();
                    return;
                }
                if (!isCurrent()) { resolve(); return; }
                if (error?.code === 'restrictions-unavailable') {
                    // Sin permisos de la empresa anterior a la vista.
                    setUserRoles({ documents: {}, folders: {} });
                    // Spec §3: la empresa muestra error y no lista carpetas.
                    rowsRef.current = [];
                    setData({ loading: false, columns: [], source: [], rows: [], hydrating: false, error: 'restrictions' });
                    notification.error({
                        message: 'No se pudo abrir la empresa',
                        description: 'No se pudieron leer las restricciones de acceso. Intente «Actualizar» en unos segundos.',
                        placement: 'topRight'
                    });
                    resolve();
                    return;
                }
                console.error('Error retrieving folder roles:', error);
                reject(error);
            });
    });

    return {
        folders: {
            data: { ...folders },
            set: setFolders,
            load: loadFolders,
            routes: { data: routes, set: setRoutesTracked },
            list: {
                ...data,
                set: setData,
                fileItem,
                ensureAllNames,
                ensureAccountingKnown,
                patchRow,
                removeRow,
            },
            current: {
                reference: { data: reference, set: setReference },
                company: { data: company, set: setCompanyCtx },
                access: () => accessRef.current.value,
                reloadAccess: () => accessFor({ force: true }),
                isClosing: { data: isClosing, set: setIsClosing },
                isUpload: { data: isUpload, set: setIsUpload },
                file: { ...file, set: setFile },
                user: {
                    roles: { ...userRoles, set: setUserRoles },
                    isRootFolder: { data: isRootFolder, set: setIsRootFolder },
                    isRoot: { data: isRoot, set: setIsRoot },
                    isAdmin: { data: isAdmin, set: setIsAdmin },
                    permissions: { ...permissions, set: setPermissions }
                }
            }
        }
    };
};

export const ListFolders = () => {
    const dispatch = useDispatch();
    const roles = useSelector(state => state.get('roles').get('roles').roles);
    const docs = useSelector(state => state.get('documents').get('read'));
    const { folders, templates, search } = useDocuments();
    const { current } = folders;

    // Estado de la carga masiva (componente compartido en documents/bulk_uploader.js).
    const [bulkOpen, setBulkOpen] = useState(false);
    // Estado de la contabilización masiva (documents/bulk_accounting.js).
    const [bulkAccOpen, setBulkAccOpen] = useState(false);
    // Filtro "Sin contabilizar" (lib/unaccounted_filter). `checking` mientras
    // se leen las filas viejas que no dicen si tienen contabilización.
    const [onlyUnaccounted, setOnlyUnaccounted] = useState(false);
    const [checking, setChecking] = useState(false);
    // Paginacion controlada: la pagina vale solo para la vista (carpeta +
    // filtro) donde se eligio; al cambiar de carpeta vuelve a la 1. El tamano
    // de pagina que elige el usuario si persiste entre carpetas.
    const [pageSt, setPageSt] = useState(null);
    const [pageSize, setPageSize] = useState(10);

    // Al cambiar de carpeta el filtro se apaga: aplica a la carpeta abierta.
    useEffect(() => { setOnlyUnaccounted(false); }, [current.reference.data]);

    const toggleUnaccounted = async () => {
        if (onlyUnaccounted) { setOnlyUnaccounted(false); return; }
        setChecking(true);
        try {
            await folders.list.ensureAccountingKnown();
            setOnlyUnaccounted(true);
        } catch (e) {
            notification.error({ message: 'No se pudo aplicar el filtro', description: 'Intenta de nuevo.', placement: 'topRight' });
        } finally {
            setChecking(false);
        }
    };

    // «Marcar como contabilizados»: solo los que ya tienen la contabilización
    // cargada y les falta la marca. Igual que el botón del cajón: a nombre de
    // quien lo pulsa y con la fecha de hoy (markAccounted). Lo ve quien puede
    // marcar en el cajón (permiso de contabilizar y la carpeta lo permite).
    const [marking, setMarking] = useState(false);
    const canMarkHere = !!current.user.roles?.documents?.accounting
        && closedFolderAccess({ globalRole: roles, isClosing: current.isClosing.data }).drawerWrite;
    const pendingMarks = onlyUnaccounted && canMarkHere ? markPendingRows(folders.list.rows) : [];
    const markPending = () => {
        const targets = pendingMarks.slice();
        Modal.confirm({
            title: 'Marcar como contabilizados',
            content: `Se marcarán ${targets.length} documento(s) de esta carpeta que ya tienen la contabilización cargada. La marca quedará a su nombre con la fecha de hoy.`,
            okText: 'Marcar',
            cancelText: 'Cancelar',
            onOk: async () => {
                setMarking(true);
                let fallidos = 0;
                for (const row of targets) {
                    try {
                        // eslint-disable-next-line no-await-in-loop
                        await dispatch(filesActions.markAccounted({ roles: current.user.roles.documents, reference: row.ref }));
                        // eslint-disable-next-line no-await-in-loop
                        const doc = await row.ref.get();
                        folders.list.patchRow(row.fileId, { accounted: doc.data()?.accounted || null });
                    } catch (e) {
                        fallidos += 1;
                    }
                }
                setMarking(false);
                if (fallidos) {
                    notification.warning({ message: 'Marcar como contabilizados', description: `Se marcaron ${targets.length - fallidos} de ${targets.length}. ${fallidos} no se pudieron marcar y siguen en el filtro.`, placement: 'topRight' });
                } else {
                    notification.success({ message: 'Marcar como contabilizados', description: `Se marcaron ${targets.length} documento(s).`, placement: 'topRight' });
                }
            },
        });
    };

    const onAddFile = () => folders.set({ ...folders.data, isLoad: true });

    useEffect(() => {
        if (folders.data.isLoad && folders.current.reference.data) {
            folders.set({ ...folders.data, isLoad: false });
            // force: este efecto solo se dispara tras una MUTACIoN (subir,
            // borrar, cerrar, abrir, permisos), asi que hay que invalidar la
            // cache de la carpeta. Navegar no pasa por aqui.
            folders.load(folders.current.reference.data, { force: true });
        }
    }, [folders]);

    // Carpetas y archivos en UNA sola lista ordenada por nombre, como siempre.
    // Las filas de archivo ya vienen resueltas (ver resolveRows), así que aquí
    // solo se convierten a JSX y se ordenan junto con las de carpeta.
    // Con el filtro activo solo se listan los documentos directos de la
    // carpeta que no están contabilizados; las subcarpetas se ocultan.
    const unaccountedRows = onlyUnaccounted ? filterUnaccounted(folders.list.rows) : [];
    const dataSource = [
        ...(onlyUnaccounted ? [] : (folders.list.source || [])),
        ...(onlyUnaccounted
            ? unaccountedRows.map(r => folders.list.fileItem(r, { flagMarkPending: true }))
            : (folders.list.rows || []).map(r => folders.list.fileItem(r))),
    ].sort((a, b) => (a?.sorted || '').localeCompare(b?.sorted || '', 'es'));

    const listViewKey = viewKey(current.reference.data?.path, onlyUnaccounted);
    // Al cambiar de vista se olvida la pagina guardada (ajuste de estado en render).
    if (syncPageState(pageSt, listViewKey) !== pageSt) setPageSt(null);

    return (
        <>
        <List
            // key: al prender o apagar el filtro la paginación vuelve a la página 1.
            key={onlyUnaccounted ? 'sin-contabilizar' : 'todos'}
            bordered={true}
            loading={folders.list.loading}
            itemLayout="vertical"
            size="small"
            pagination={{
                current: pageFor(pageSt, listViewKey),
                pageSize,
                onChange: (page, size) => {
                    setPageSize(size);
                    setPageSt(pageState(listViewKey, size !== pageSize ? 1 : page));
                },
            }}
            locale={{ emptyText: folders.list.error === 'restrictions'
                ? 'No se pudieron leer las restricciones de acceso de esta empresa.'
                : onlyUnaccounted ? 'Todos los documentos de esta carpeta están contabilizados' : 'No hay informacion para esta carpeta' }}
            dataSource={dataSource}
            renderItem={item => (
                <List.Item
                    key={item.key}
                    onMouseEnter={item.onHover}
                    actions={item.actions}>
                    <List.Item.Meta title={item.title} description={item.description}
                        avatar={<Avatar style={{ color: '#f56a00', backgroundColor: '#fde3cf' }} icon={item.avatar} />} />
                    {item.content}
                </List.Item>
            )}
            header={
                <>
                    {current.user.roles?.folders?.read && current.user.roles?.folders?.create && closedFolderAccess({ globalRole: roles, isClosing: current.isClosing.data }).folderWrite ?
                        <>
                            <Button type='dashed'
                                onClick={() => folders.set({ ...folders.data, name: "", visible: true, loading: false, update: false })} >
                                Nueva carpeta
                            </Button>
                            <Divider type="vertical" />
                            <Button type='dashed'
                                onClick={() => templates.load({ userRoles: current.user.roles })}
                            > Clonar carpeta </Button>
                        </> : null}

                    {docs?.data && current.isUpload.data && closedFolderAccess({ globalRole: roles, isClosing: current.isClosing.data }).folderWrite && current.user.roles?.folders?.read && current.user.roles?.documents?.create ?
                        <>
                            <Divider type="vertical" />
                            <DocumentUpload reference={current.reference.data} onAddFile={onAddFile} />
                            <Divider type="vertical" />
                            <Button type='dashed' onClick={() => setBulkOpen(true)}>
                                Carga masiva
                            </Button>
                        </> : current.user.isRoot.data === true ?
                        /* Root siempre puede abrir la carga masiva, incluso en la
                           RAÍZ del cliente (donde no hay subida normal): ahí el
                           uploader fuerza el modo árbol y solo acepta carpetas. */
                        <>
                            <Divider type="vertical" />
                            <Button type='dashed' onClick={() => setBulkOpen(true)}>
                                Carga masiva
                            </Button>
                        </> : null}

                    {docs?.data && current.isUpload.data ?
                        <>
                            <Divider type="vertical" />
                            <Button type='dashed'
                                onClick={() => search.set({ visible: true, loading: false })} >
                                Buscar archivos
                            </Button>
                        </> : null}

                    {/* El contenido de la carpeta se guarda en memoria durante la
                        sesión, así que volver a ella es instantáneo. Esto fuerza
                        una relectura para ver lo que hayan subido otros mientras
                        tanto. */}
                    {docs?.data && current.reference.data ?
                        <>
                            <Divider type="vertical" />
                            <Button type='dashed'
                                loading={folders.list.loading}
                                onClick={() => folders.load(current.reference.data, { force: true, refreshAccess: true })} >
                                Actualizar
                            </Button>
                        </> : null}

                    {/* Contabilización masiva: NO depende de isUpload (contabilizar
                        no es subir documentos de negocio). La regla vive en
                        lib/bulk_accounting_visibility.js — incluye el escape de
                        root en carpeta cerrada, igual que contabilizar individual
                        (file.js:791,865) y que "Carga masiva" aquí arriba. */}
                    {docs?.data && canSeeBulkAccounting({
                        globalRole: null,
                        effective: current.user.roles?.documents,
                        isSuperAdmin: current.user.isRoot.data,
                        isClosed: current.isClosing.data,
                        hasDirectFiles: (folders.list.rows || []).length > 0,
                    }) ?
                        <>
                            <Divider type="vertical" />
                            <Button type='dashed' onClick={() => setBulkAccOpen(true)}>
                                Contabilización masiva
                            </Button>
                        </> : null}

                    {/* Filtro "Sin contabilizar": lo ve quien ve la marca de
                        contabilizado (lib/accounted_visibility.js). Es solo
                        lectura, así que también sirve con la carpeta cerrada. */}
                    {docs?.data && (folders.list.rows || []).length > 0 && seesAccountedAlways({
                        isSuperAdmin: current.user.isRoot.data,
                        isAdmin: current.user.isAdmin.data,
                        canAccount: current.user.roles?.documents?.accounting,
                    }) ?
                        <>
                            <Divider type="vertical" />
                            <Button type={onlyUnaccounted ? 'primary' : 'dashed'}
                                loading={checking}
                                onClick={toggleUnaccounted}>
                                {onlyUnaccounted ? 'Ver todos' : 'Sin contabilizar'}
                            </Button>
                            {onlyUnaccounted ?
                                <Tag color='orange' style={{ marginLeft: 8 }}>
                                    {unaccountedRows.length} sin contabilizar
                                </Tag> : null}
                            {/* Solo si hay alguno con la contabilización cargada y sin marca. */}
                            {pendingMarks.length > 0 ?
                                <Button type='primary' ghost loading={marking} onClick={markPending}>
                                    Marcar como contabilizados ({pendingMarks.length})
                                </Button> : null}
                        </> : null}

                    {docs?.data ?
                        <div style={{ marginTop: 15 }}>
                            <Breadcrumb>
                                {folders.routes.data?.map((route, index) => (
                                    <Breadcrumb.Item key={index} >
                                        <Button type="link" onClick={() => {
                                            if (current.reference.data !== route.reference) {
                                                const index = folders.routes.data.findIndex(item => item.reference === route.reference);
                                                // slice (no splice): splice mutaba el array de estado en sitio
                                                // y los closures viejos (ej. deleteFolder) quedaban viendo el
                                                // array decapitado — routes[0] pasaba a ser una subcarpeta.
                                                const tmp_routes = folders.routes.data.slice(0, index + 1);

                                                folders.load(route.reference);
                                                folders.routes.set(tmp_routes);
                                            }
                                        }} > {route.name} </Button>
                                    </Breadcrumb.Item>
                                ))}
                            </Breadcrumb>
                        </div> : null}
                </>
            } />

        <BulkUploader
            open={bulkOpen}
            onClose={() => setBulkOpen(false)}
            targetFolderRef={current.reference.data || null}
            targetFolderName={folders.routes.data?.[folders.routes.data.length - 1]?.name || ''}
            onComplete={() => folders.set({ ...folders.data, isLoad: true })}
            // Estructura de árbol: rango ≥ 60 (contador, supervisora, root).
            // Sin límites de lote: el personal de nivel global, como siempre.
            canUploadTree={canRestrict(roles)}
            unlimited={current.user.isRoot.data === true}
        />

        <BulkAccounting
            open={bulkAccOpen}
            onClose={() => setBulkAccOpen(false)}
            targetFolderRef={current.reference.data || null}
            targetFolderName={folders.routes.data?.[folders.routes.data.length - 1]?.name || ''}
            onComplete={() => folders.set({ ...folders.data, isLoad: true })}
        />
        </>
    );
};
